import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';

/** Bloque OTP unificado (estilo Canal de Ventas): casillas + botón validar. */
@Component({
  selector: 'app-otp-verification-block',
  templateUrl: './otp-verification-block.component.html',
  styleUrls: ['./otp-verification-block.component.css'],
  standalone: false
})
export class OtpVerificationBlockComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  /** Destino enmascarado (ej. j***@gmail.com). */
  @Input() maskedDestination = '';
  /** Segundos de validez / espera para reenviar. */
  @Input() expiresInSeconds = 300;
  /** Si cambia, reinicia el temporizador (ej. tras reenviar). */
  @Input() timerRestartKey = 0;
  @Input() errorMessage: string | null = null;
  @Input() validating = false;
  @Input() showCancelButton = true;
  @Input() cancelLabel = 'Cancelar';
  @Input() submitLabel = 'Verificar código';
  @Input() showResendLink = true;
  @Input() hintText = '';
  @Input() expiredMessage: string | null = null;
  /** Si false, oculta el párrafo introductorio (el padre ya lo muestra). */
  @Input() showLeadText = true;
  /** Longitud del código (4 descarga Canal / 5 reset password y Certifirma Digital Físico). */
  @Input() otpLength = 5;

  @Output() submitCode = new EventEmitter<{ code: string }>();
  @Output() resend = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  @ViewChildren('digitInput') digitInputs!: QueryList<ElementRef<HTMLInputElement>>;

  digits: string[] = [];
  remainingSeconds = 0;
  expired = false;

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private startTime = 0;
  /** Evita reenviar el mismo código completo (p. ej. tras error, hasta que el usuario lo cambie). */
  private lastAutoSubmittedCode: string | null = null;

  ngOnInit(): void {
    this.resetDigits();
    this.startTimer();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.focusDigit(0), 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['otpLength'] && !changes['otpLength'].firstChange) {
      this.resetDigits();
    }
    if (changes['timerRestartKey'] && !changes['timerRestartKey'].firstChange) {
      this.resetDigits();
      this.startTimer();
      setTimeout(() => this.focusDigit(0), 0);
    }
    if (changes['expiresInSeconds'] && !this.intervalId) {
      this.remainingSeconds = Math.max(0, this.expiresInSeconds);
    }
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  get codeOtp(): string {
    return this.digits.join('');
  }

  get countdownLabel(): string {
    const m = Math.floor(this.remainingSeconds / 60);
    const s = this.remainingSeconds % 60;
    return `${m < 10 ? '0' : ''}${m}:${s.toString().padStart(2, '0')}`;
  }

  get canSubmit(): boolean {
    return !this.expired && !this.validating && this.codeOtp.length === this.otpLength;
  }

  get canResend(): boolean {
    return this.expired || this.remainingSeconds <= 0;
  }

  get displayExpiredMessage(): string {
    if (this.expiredMessage) return this.expiredMessage;
    return this.showResendLink
      ? 'El código ha expirado. Puedes solicitar uno nuevo con «Reenviar».'
      : 'El código ha expirado. Debe solicitar uno nuevo desde el paso anterior.';
  }

  get digitIndexes(): number[] {
    return Array.from({ length: this.otpLength }, (_, i) => i);
  }

  trackByIndex(index: number): number {
    return index;
  }

  onDigitInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/\D/g, '');
    if (!raw) {
      this.digits[index] = '';
      input.value = '';
      this.lastAutoSubmittedCode = null;
      return;
    }
    // Pegado de varios dígitos en una casilla
    if (raw.length > 1) {
      this.applyPaste(raw, index);
      return;
    }
    this.digits[index] = raw;
    input.value = raw;
    if (index < this.otpLength - 1) {
      this.focusDigit(index + 1);
    }
    this.tryAutoSubmit();
  }

  onDigitKeydown(index: number, event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace') {
      if (input.value) {
        this.digits[index] = '';
        input.value = '';
      } else if (index > 0) {
        this.focusDigit(index - 1);
        const prev = this.digitInputs?.get(index - 1)?.nativeElement;
        if (prev) {
          this.digits[index - 1] = '';
          prev.value = '';
        }
      }
      this.lastAutoSubmittedCode = null;
      event.preventDefault();
      return;
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      this.focusDigit(index - 1);
      event.preventDefault();
    }
    if (event.key === 'ArrowRight' && index < this.otpLength - 1) {
      this.focusDigit(index + 1);
      event.preventDefault();
    }
    if (event.key === 'Enter' && this.canSubmit) {
      this.onSubmit();
      event.preventDefault();
    }
  }

  onDigitPaste(index: number, event: ClipboardEvent): void {
    event.preventDefault();
    const text = (event.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (!text) return;
    this.applyPaste(text, index);
  }

  onSubmit(): void {
    if (!this.canSubmit) return;
    this.lastAutoSubmittedCode = this.codeOtp;
    this.submitCode.emit({ code: this.codeOtp });
  }

  /** Envía solo cuando el código está completo (escritura o pegado). */
  private tryAutoSubmit(): void {
    if (!this.canSubmit) return;
    if (this.lastAutoSubmittedCode === this.codeOtp) return;
    this.onSubmit();
  }

  onResend(): void {
    if (!this.canResend) return;
    this.resend.emit();
  }

  onCancel(): void {
    this.cancel.emit();
  }

  private applyPaste(text: string, startIndex: number): void {
    const chars = text.slice(0, this.otpLength - startIndex).split('');
    chars.forEach((ch, i) => {
      const idx = startIndex + i;
      this.digits[idx] = ch;
      const el = this.digitInputs?.get(idx)?.nativeElement;
      if (el) el.value = ch;
    });
    const next = Math.min(startIndex + chars.length, this.otpLength - 1);
    this.focusDigit(next);
    this.tryAutoSubmit();
  }

  private resetDigits(): void {
    this.digits = Array.from({ length: this.otpLength }, () => '');
    this.lastAutoSubmittedCode = null;
  }

  private focusDigit(index: number): void {
    const el = this.digitInputs?.get(index)?.nativeElement;
    if (el) {
      el.focus();
      el.select();
    }
  }

  private startTimer(): void {
    this.clearTimer();
    this.remainingSeconds = Math.max(0, this.expiresInSeconds);
    this.expired = this.remainingSeconds <= 0;
    this.startTime = Date.now();
    if (!this.expired) {
      this.intervalId = setInterval(() => this.tick(), 1000);
    }
  }

  private clearTimer(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private tick(): void {
    const elapsed = Math.floor((Date.now() - this.startTime) / 1000);
    this.remainingSeconds = Math.max(0, this.expiresInSeconds - elapsed);
    if (this.remainingSeconds === 0) {
      this.expired = true;
      this.clearTimer();
    }
  }
}
